import React, { useState, useEffect } from 'react'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import Icon from '../../../components/AppIcon'
import { localDateKey, validateOptionalDateOfBirth } from '../lib/goldPurchaseSnapshot.mjs'

const ID_TYPES = [
  { value: '', label: 'Select ID Type' },
  { value: 'driver_license', label: 'Driver License' },
  { value: 'identification_card', label: 'Identification Card' },
  { value: 'passport', label: 'Passport' },
  { value: 'military_id', label: 'Military ID' },
  { value: 'immigration_card', label: 'Immigration Card' },
  { value: 'other', label: 'Other' },
]

const CustomerInfoForm = ({ customer, onUpdate, onValidate }) => {
  const [errors, setErrors] = useState({})
  const today = localDateKey(new Date())

  const handleChange = (field, value) => {
    onUpdate({ ...customer, [field]: value })
  }

  const validate = () => {
    const newErrors = {}

    if (!customer.firstName?.trim()) {
      newErrors.firstName = 'First name is required'
    }
    if (!customer.lastName?.trim()) {
      newErrors.lastName = 'Last name is required'
    }
    if (!customer.idType) {
      newErrors.idType = 'ID type is required'
    }
    const dateOfBirthError = validateOptionalDateOfBirth(customer.dateOfBirth, today)
    if (dateOfBirthError) newErrors.dateOfBirth = dateOfBirthError

    // ID-specific validation
    if (customer.idType === 'driver_license' || customer.idType === 'identification_card') {
      if (!customer.idNumber?.trim()) {
        newErrors.idNumber = 'ID number is required'
      }
      if (!customer.idState?.trim()) {
        newErrors.idState = 'State is required'
      }
      if (!customer.idExpiration) {
        newErrors.idExpiration = 'Expiration date is required'
      }
    } else if (customer.idType === 'passport') {
      if (!customer.idNumber?.trim()) {
        newErrors.idNumber = 'Passport number is required'
      }
      if (!customer.idCountry?.trim()) {
        newErrors.idCountry = 'Country is required'
      }
      if (!customer.idExpiration) {
        newErrors.idExpiration = 'Expiration date is required'
      }
    } else if (customer.idType === 'military_id' || customer.idType === 'immigration_card') {
      if (!customer.idNumber?.trim()) newErrors.idNumber = 'ID number is required'
    } else if (customer.idType === 'other') {
      if (!customer.idOtherReason?.trim()) {
        newErrors.idOtherReason = 'Reason is required'
      }
      if (!customer.idOtherDetails?.trim()) {
        newErrors.idOtherDetails = 'Details are required'
      }
    }

    setErrors(newErrors)
    const isValid = Object.keys(newErrors).length === 0
    if (onValidate) onValidate(isValid)
    return isValid
  }

  useEffect(() => {
    validate()
  }, [customer])

  const renderIdFields = () => {
    switch (customer.idType) {
      case 'driver_license':
      case 'identification_card':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  {customer.idType === 'driver_license' ? 'DL Number' : 'ID Number'} *
                </label>
                <input
                  type="text"
                  value={customer.idNumber || ''}
                  onChange={(e) => handleChange('idNumber', e.target.value)}
                  className={`w-full px-3 py-2.5 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                    errors.idNumber ? 'border-destructive/30' : 'border-border/60'
                  }`}
                  placeholder="Enter number"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">State Issued *</label>
                <input
                  type="text"
                  value={customer.idState || ''}
                  onChange={(e) => handleChange('idState', e.target.value.toUpperCase())}
                  maxLength={2}
                  className={`w-full px-3 py-2.5 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                    errors.idState ? 'border-destructive/30' : 'border-border/60'
                  }`}
                  placeholder="e.g., FL"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Expiration Date *</label>
              <input
                type="date"
                value={customer.idExpiration || ''}
                onChange={(e) => handleChange('idExpiration', e.target.value)}
                className={`w-full px-3 py-2.5 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                  errors.idExpiration ? 'border-destructive/30' : 'border-border/60'
                }`}
              />
            </div>
          </div>
        )

      case 'passport':
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Passport Number *</label>
                <input
                  type="text"
                  value={customer.idNumber || ''}
                  onChange={(e) => handleChange('idNumber', e.target.value)}
                  className={`w-full px-3 py-2.5 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                    errors.idNumber ? 'border-destructive/30' : 'border-border/60'
                  }`}
                  placeholder="Enter passport number"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Country of Origin *</label>
                <input
                  type="text"
                  value={customer.idCountry || ''}
                  onChange={(e) => handleChange('idCountry', e.target.value)}
                  className={`w-full px-3 py-2.5 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                    errors.idCountry ? 'border-destructive/30' : 'border-border/60'
                  }`}
                  placeholder="e.g., USA"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Expiration Date *</label>
              <input
                type="date"
                value={customer.idExpiration || ''}
                onChange={(e) => handleChange('idExpiration', e.target.value)}
                className={`w-full px-3 py-2.5 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                  errors.idExpiration ? 'border-destructive/30' : 'border-border/60'
                }`}
              />
            </div>
          </div>
        )

      case 'military_id':
      case 'immigration_card':
        return (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="block text-xs font-medium text-muted-foreground">
              ID Number *
              <input
                type="text"
                value={customer.idNumber || ''}
                onChange={(e) => handleChange('idNumber', e.target.value)}
                className={`mt-1 w-full px-3 py-2.5 border rounded-lg text-base md:text-sm ${errors.idNumber ? 'border-destructive/30' : 'border-border/60'}`}
              />
            </label>
            <label className="block text-xs font-medium text-muted-foreground">
              Issuing Country (optional)
              <input
                type="text"
                value={customer.idCountry || ''}
                onChange={(e) => handleChange('idCountry', e.target.value)}
                className="mt-1 w-full px-3 py-2.5 border border-border/60 rounded-lg text-base md:text-sm"
              />
            </label>
            <label className="block text-xs font-medium text-muted-foreground">
              Expiration Date (optional)
              <input
                type="date"
                value={customer.idExpiration || ''}
                onChange={(e) => handleChange('idExpiration', e.target.value)}
                className="mt-1 w-full px-3 py-2.5 border border-border/60 rounded-lg text-base md:text-sm"
              />
            </label>
          </div>
        )

      case 'other':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Reason *</label>
              <input
                type="text"
                value={customer.idOtherReason || ''}
                onChange={(e) => handleChange('idOtherReason', e.target.value)}
                className={`w-full px-3 py-2.5 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                  errors.idOtherReason ? 'border-destructive/30' : 'border-border/60'
                }`}
                placeholder="Why are you using 'Other'?"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Other Details *</label>
              <textarea
                value={customer.idOtherDetails || ''}
                onChange={(e) => handleChange('idOtherDetails', e.target.value)}
                rows={2}
                className={`w-full px-3 py-2.5 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none ${
                  errors.idOtherDetails ? 'border-destructive/30' : 'border-border/60'
                }`}
                placeholder="Provide additional details..."
              />
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="space-y-4">
      {/* Name Fields */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">First Name *</label>
          <input
            type="text"
            value={customer.firstName || ''}
            onChange={(e) => handleChange('firstName', e.target.value)}
            className={`w-full px-3 py-2.5 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
              errors.firstName ? 'border-destructive/30' : 'border-border/60'
            }`}
            placeholder="First name"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Last Name *</label>
          <input
            type="text"
            value={customer.lastName || ''}
            onChange={(e) => handleChange('lastName', e.target.value)}
            className={`w-full px-3 py-2.5 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
              errors.lastName ? 'border-destructive/30' : 'border-border/60'
            }`}
            placeholder="Last name"
          />
        </div>
      </div>

      <div>
        <label htmlFor="customer-date-of-birth" className="block text-xs font-medium text-muted-foreground mb-1">
          Date of Birth (optional)
        </label>
        <input
          id="customer-date-of-birth"
          type="date"
          value={customer.dateOfBirth || ''}
          max={today}
          onChange={(e) => handleChange('dateOfBirth', e.target.value)}
          aria-invalid={!!errors.dateOfBirth}
          aria-describedby={errors.dateOfBirth ? 'customer-date-of-birth-error' : undefined}
          className={`w-full px-3 py-2.5 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${errors.dateOfBirth ? 'border-destructive/30' : 'border-border/60'}`}
        />
        {errors.dateOfBirth && <p id="customer-date-of-birth-error" className="mt-1 text-xs text-destructive">{errors.dateOfBirth}</p>}
      </div>

      {/* Contact Fields */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Phone (optional)</label>
          <input
            type="tel"
            value={customer.phone || ''}
            onChange={(e) => handleChange('phone', e.target.value)}
            className="w-full px-3 py-2.5 border border-border/60 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            placeholder="Phone number"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Email (optional)</label>
          <input
            type="email"
            value={customer.email || ''}
            onChange={(e) => handleChange('email', e.target.value)}
            className="w-full px-3 py-2.5 border border-border/60 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            placeholder="Email address"
          />
        </div>
      </div>

      {/* ID Type Selection */}
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">ID Type *</label>
        <select
          value={customer.idType || ''}
          onChange={(e) => handleChange('idType', e.target.value)}
          className={`w-full px-3 py-2.5 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
            errors.idType ? 'border-destructive/30' : 'border-border/60'
          }`}
        >
          {ID_TYPES.map((type) => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
      </div>

      {/* ID-specific fields */}
      {customer.idType && renderIdFields()}

      {/* Address Fields */}
      <div className="pt-3 border-t border-border/60">
        <h4 className="text-xs font-medium text-muted-foreground mb-3">Address (optional)</h4>
        <div className="space-y-3">
          <div>
            <input
              type="text"
              value={customer.street || ''}
              onChange={(e) => handleChange('street', e.target.value)}
              className="w-full px-3 py-2.5 border border-border/60 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              placeholder="Street address"
            />
          </div>
          {/* City flexes; State (2 chars) and ZIP stay narrow so nothing truncates on phones */}
          <div className="grid grid-cols-[minmax(0,1fr)_4.5rem_5.5rem] gap-2 sm:grid-cols-3 sm:gap-3">
            <div>
              <input
                type="text"
                value={customer.city || ''}
                onChange={(e) => handleChange('city', e.target.value)}
                className="w-full px-3 py-2.5 border border-border/60 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                placeholder="City"
              />
            </div>
            <div>
              <input
                type="text"
                value={customer.state || ''}
                onChange={(e) => handleChange('state', e.target.value.toUpperCase())}
                maxLength={2}
                className="w-full px-3 py-2.5 border border-border/60 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                placeholder="State"
              />
            </div>
            <div>
              <input
                type="text"
                value={customer.zip || ''}
                onChange={(e) => handleChange('zip', e.target.value)}
                className="w-full px-3 py-2.5 border border-border/60 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                placeholder="ZIP"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">Notes (optional)</label>
        <textarea
          value={customer.notes || ''}
          onChange={(e) => handleChange('notes', e.target.value)}
          rows={2}
          className="w-full px-3 py-2.5 border border-border/60 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
          placeholder="Additional notes..."
        />
      </div>
    </div>
  )
}

export default CustomerInfoForm
